import { useCallback, useEffect, useState } from "react";
import { KompetenceCardCategory, KompetenceCardListType, KompetenceCatagory } from "./kompetenceCardList";
import { removeActiveLaegekompetenceMeta, setActiveLaegekompetenceMeta, setIsLoadingList, toggleLaegekompetenceListRefreshPing } from "./kompetenceCardSlice";
import IdAndCount from "core/sharedmodels/IdAndCount";
import useStamdataApi from "core/hooks/useStamdataApi";
import { genericGroupByAndMap, sortKompetenceBySortOrder } from "core/utils";
import { nameof } from "ts-simple-nameof";
import { LaegekompetenceMeta } from "core/sharedmodels/kompetence/laegekompetenceMeta";
import { KompetenceListViewTypeEnum } from "core/sharedmodels/kompetence/kompetenceListViewTypeEnum";
import { LaegekompetenceStatusEnum } from "core/sharedmodels/kompetence/laegekompetenceStatusEnum";
import useLogbogApi from "core/hooks/useLogbogApi";
import { Localizer } from "infrastructure/localization/localizer";
import { useAppDispatch } from "app/hooks";
import useScreenResolution from "core/hooks/useScreenResolution";
import useUser from "core/hooks/useUser";
import { ForloebState } from "core/forloebSlice";

const filterSortAndSplitFetchedKompetencer = (laegekompetencer: LaegekompetenceMeta[], kompetenceListViewType: KompetenceListViewTypeEnum,
  filterBrugerKompetencerByMaalbeskrivelse: boolean, forloebBrugerMaalbeskrivelseId: string,
  forloebId: string, brugerMaalbeskrivelseId?: string
) => {
  filterBrugerKompetencerByMaalbeskrivelse = filterBrugerKompetencerByMaalbeskrivelse && !!brugerMaalbeskrivelseId;

  const kompetencerOnBruger = laegekompetencer.filter(lk => !lk.forloebIds.includes(forloebId) && (!filterBrugerKompetencerByMaalbeskrivelse || lk.brugerMaalbeskrivelseId === brugerMaalbeskrivelseId));
  const kompetencerOnBrugerSorted = sortKompetenceBySortOrder(kompetencerOnBruger);
  kompetencerOnBrugerSorted.SortByThenBy(nameof<LaegekompetenceMeta>(x => x.isFavorite), nameof<LaegekompetenceMeta>(x => x.sortOrder), false);

  const kompetencerOnForloeb = laegekompetencer.filter(lk => lk.forloebIds.includes(forloebId) && lk.brugerMaalbeskrivelseId === forloebBrugerMaalbeskrivelseId);
  const kompetencerOnForloebSorted = sortKompetenceBySortOrder(kompetencerOnForloeb);
  kompetencerOnForloebSorted.SortByThenBy(nameof<LaegekompetenceMeta>(x => x.isFavorite), nameof<LaegekompetenceMeta>(x => x.sortOrder), false);

  const samletSortedListe = sortKompetenceBySortOrder(kompetencerOnBruger.concat(kompetencerOnForloeb));

  return {
      kompetencerOnBruger: kompetenceListViewType === KompetenceListViewTypeEnum.Standard ? samletSortedListe : kompetencerOnBrugerSorted,
      kompetencerOnForloeb: kompetenceListViewType === KompetenceListViewTypeEnum.Standard ? [] : kompetencerOnForloebSorted,
  }
}

const filterLaegeKompetenceMetas = (metas: LaegekompetenceMeta[], kompetenceStatus?: LaegekompetenceStatusEnum, search?: string) => {
  const searchToLower = search?.toLowerCase();

  const toLowerIncludes = (val: string, searchString: string) => val.toLowerCase().includes(searchString);

  return metas
      // Filter by status
      .filter(lk => kompetenceStatus === undefined || lk.status === kompetenceStatus)
      // Filter by search
      .filter(lk => !searchToLower ||
          (
              toLowerIncludes(lk.fullKompetenceTitleWithNumber, searchToLower)
              || toLowerIncludes(lk.userNavn, searchToLower)
              || (!!lk.underoverskrift && toLowerIncludes(lk.underoverskrift, searchToLower))
              || toLowerIncludes(lk.konkretisering, searchToLower)
              || toLowerIncludes(lk.laeringsstrategi, searchToLower)
              || toLowerIncludes(lk.kompetenceVurderingsmetoder, searchToLower)
          )
      )
}

type useKompetenceCardListCategoriesOptions = {
  kompetenceCardListType: KompetenceCardListType;
  kompetenceListViewType: KompetenceListViewTypeEnum;
  disableFileCount: boolean;
  refreshOnChange?: boolean;
}

type useKompetenceCardListCategoriesFilter = {
  searchParam?: string;
  kompetenceStatus?: LaegekompetenceStatusEnum;
}

type useKompetenceCardListCategoriesProps = {
  options: useKompetenceCardListCategoriesOptions,
  filter: useKompetenceCardListCategoriesFilter,
  forloebState: ForloebState,
  selectedBrugerMaalbeskrivelseId?: string,
  activeLaegeKompetenceId?: string,
}

const useKompetenceCardListCategories = ({
  filter,
  options,
  forloebState,
  selectedBrugerMaalbeskrivelseId,
  activeLaegeKompetenceId
}: useKompetenceCardListCategoriesProps) => {

  const dispatch = useAppDispatch();
  const { isMobile } = useScreenResolution();
  const { currentUser } = useUser();

  const { stamdataKompetenceApi } = useStamdataApi();
  const { logbogLaegeKompetenceApi } = useLogbogApi();

  const [laegeKompetencerMeta, setLaegeKompetencerMeta] = useState<LaegekompetenceMeta[]>([]);
  const [laegekompetenceCategoriesState, setLaegekompetenceCategoriesState] = useState<KompetenceCardCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [kompetenceIdsAndFileCounts, setKompetenceIdsAndFileCounts] = useState<IdAndCount[]>([]);

  const isKlarmeldingerPage = options.kompetenceCardListType === KompetenceCardListType.KlarmeldteKompetencerByVejleder;

  const brugerMaalbeskrivelseIdToUse = selectedBrugerMaalbeskrivelseId ?? forloebState.brugerMaalbeskrivelseId;
  const shouldFetchViaBrugerMaalbeskrivelse = !!(selectedBrugerMaalbeskrivelseId && selectedBrugerMaalbeskrivelseId !== forloebState.brugerMaalbeskrivelseId); 

  useEffect(() => {
    const fetchKompetenceFilesCounts = async () => {
      const kompetenceIds = laegeKompetencerMeta.map(x => x.kompetenceId);

      if (options.disableFileCount || kompetenceIds.length <= 0 || !forloebState.regionId || !forloebState.brugerMaalbeskrivelseId) {
        return;
      }

      const specialeSpecifikkeFilesCount = await stamdataKompetenceApi.getSpecialeSpecificFilesCount({
        kompetenceIds: kompetenceIds,
        regionId: forloebState.regionId,
        brugerMaalbeskrivelseId: forloebState.brugerMaalbeskrivelseId
      })

      setKompetenceIdsAndFileCounts(specialeSpecifikkeFilesCount);
    }

    fetchKompetenceFilesCounts();
  }, [forloebState.brugerMaalbeskrivelseId, forloebState.regionId, laegeKompetencerMeta, options.disableFileCount, stamdataKompetenceApi]);


  const fetchLaegeKompetencer = useCallback(async () => {
    const fetch = async () => {
      setIsLoading(true);
      dispatch(setIsLoadingList(true));

      if (options.kompetenceCardListType === KompetenceCardListType.KompetencerByBruger) { 
        
        const laegeKompetencer = shouldFetchViaBrugerMaalbeskrivelse
          ? await logbogLaegeKompetenceApi.getLaegekompetencerByBrugerMaalbeskrivelse(brugerMaalbeskrivelseIdToUse)
          : await logbogLaegeKompetenceApi.getLaegekompetencerByBrugerAndUddannelseIdAndSpecialeId(forloebState.brugerId, forloebState.uddannelseId, forloebState.specialeId);
  
        setLaegeKompetencerMeta(laegeKompetencer);
  
      } else if (options.kompetenceCardListType === KompetenceCardListType.KlarmeldteKompetencerByVejleder) {
        
        const laegekompetencer = await logbogLaegeKompetenceApi.getLaegekompetenceKlarmeldinger();
        const laegekompetencerFiltered = laegekompetencer.filter(k => k.userId !== currentUser.UserId);
        setLaegeKompetencerMeta(laegekompetencerFiltered);
      }
    }

    fetch().finally(() => {
      setIsLoading(false);
      dispatch(setIsLoadingList(false));
    });
  }, [brugerMaalbeskrivelseIdToUse, currentUser.UserId, dispatch, forloebState.brugerId, forloebState.specialeId, forloebState.uddannelseId, logbogLaegeKompetenceApi, options.kompetenceCardListType, shouldFetchViaBrugerMaalbeskrivelse])

  useEffect(() => {
    fetchLaegeKompetencer();
  
  // Note: Right now we use this refresh-ping (options.refreshOnChange) for updating list (from many places). In future, we should patch the state instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetchLaegeKompetencer, options.refreshOnChange]);

  useEffect(() => {
    const generateLaegeKompetenceCategories = () => {
      if (options.kompetenceCardListType === KompetenceCardListType.KompetencerByBruger) {

        //If we fetch via bruger/uddannelse, we need to filter brugerKompetencer (so it only includes laegekompetencer on current bruger-maalbeskrivelse)
        const shouldFilterBrugerKompetencerByMaalbeskrivelse = !shouldFetchViaBrugerMaalbeskrivelse; 

        const { kompetencerOnBruger, kompetencerOnForloeb } = filterSortAndSplitFetchedKompetencer(
          laegeKompetencerMeta, 
          options.kompetenceListViewType, 
          shouldFilterBrugerKompetencerByMaalbeskrivelse, 
          forloebState.brugerMaalbeskrivelseId, 
          forloebState.id, 
          brugerMaalbeskrivelseIdToUse
        );

        const kompetencerPaaIkkeAktivMaalbeskrivelser = laegeKompetencerMeta.filter(lk => lk.brugerMaalbeskrivelseId !== forloebState.brugerMaalbeskrivelseId);
        const kompetencerPaaIkkeAktivBrugerMaalbeskrivelserGrouped = genericGroupByAndMap(kompetencerPaaIkkeAktivMaalbeskrivelser, "brugerMaalbeskrivelseId");
        const foraeldedeKompetenceCategories = Array.from(kompetencerPaaIkkeAktivBrugerMaalbeskrivelserGrouped.entries()).map(([key, value]) => ({
          laegekompetencer: value,
          title: value[0].maalbeskrivelseNavn + ` (${value.length})`,
          catagoryEnum: KompetenceCatagory.Foraeldede,
          expandByDefault: false,
          id: key 
        }));

        const newKompetenceCategories: KompetenceCardCategory[] = [
          ...(options.kompetenceListViewType !== KompetenceListViewTypeEnum.Standard ? 
            [{
              laegekompetencer: kompetencerOnForloeb,
              title: Localizer.forloebpage_InThisForloeb(),
              catagoryEnum: KompetenceCatagory.KompetencerOnForloeb,
              expandByDefault: true
            }] : []
          ),
          {
            laegekompetencer: kompetencerOnBruger,
            title: options.kompetenceListViewType === KompetenceListViewTypeEnum.Standard ? Localizer.global_allekompetencer() : Localizer.global_resterende(),
            catagoryEnum: KompetenceCatagory.OevrigeKompetencer,
            expandByDefault: true
          },
          ...foraeldedeKompetenceCategories,
        ];

        setLaegekompetenceCategoriesState(newKompetenceCategories);
      }
      else if(options.kompetenceCardListType === KompetenceCardListType.KlarmeldteKompetencerByVejleder){
        const laegeKompetencer = laegeKompetencerMeta;
        laegeKompetencer.SortByThenBy(nameof<LaegekompetenceMeta>(x => x.userNavn), nameof<LaegekompetenceMeta>(x => x.sortOrder), false);
        
        const newKompetenceCategories = [
        {
            laegekompetencer: laegeKompetencer,
            title: options.kompetenceListViewType === KompetenceListViewTypeEnum.Standard ? Localizer.global_allekompetencer() : Localizer.global_klarmeldinger(),
            catagoryEnum: KompetenceCatagory.Klarmeldinger,
            expandByDefault: true
        }];

        setLaegekompetenceCategoriesState(newKompetenceCategories);
      }
      
    }
    generateLaegeKompetenceCategories();
  }, [brugerMaalbeskrivelseIdToUse, forloebState.brugerMaalbeskrivelseId, forloebState.id, laegeKompetencerMeta, options.kompetenceCardListType, options.kompetenceListViewType, shouldFetchViaBrugerMaalbeskrivelse])

  useEffect(() => {
    const handleSetActiveKompetence = () => {

      if (isMobile) return; // When mobile, we only show active-laegekompetence when clicking on item.

      if(isLoading) return; // It list still is loading, don't set active-laegekompetence yet.

      if(laegeKompetencerMeta.length <= 0) {
        // Handle no laegekompetencer fetched: Remove outdated kompetence
        dispatch(removeActiveLaegekompetenceMeta());
        return;
      }
      
      if (activeLaegeKompetenceId) {
        // Handle previous active laegekompetence:       
        const activeKompetenceInList = laegekompetenceCategoriesState
          .flatMap(x => x.laegekompetencer)
          .find(l => activeLaegeKompetenceId === l.laegekompetenceId);

        if (!!activeKompetenceInList) {
          return;
        }
      }
      // Handle set of new active laegekompetence:
      const allLaegeKompetencerOnFirstAvailableCategory = laegekompetenceCategoriesState
        .filter(x => x.laegekompetencer
          //Get First-category with any laegeKompetencer (if kompetencer-page: also check that brugerMaalbeskrivelse matches)
          .filter(x => isKlarmeldingerPage || x.brugerMaalbeskrivelseId === brugerMaalbeskrivelseIdToUse).length > 0)
        .firstOrDefault()
        ?.laegekompetencer ?? [] as LaegekompetenceMeta[];
      
      const firstSortByPropertyName = isKlarmeldingerPage 
        ? nameof<LaegekompetenceMeta>(x => x.userNavn) 
        : nameof<LaegekompetenceMeta>(x => x.isFavorite);

      allLaegeKompetencerOnFirstAvailableCategory.SortByThenBy(firstSortByPropertyName, nameof<LaegekompetenceMeta>(x => x.sortOrder), false);
        
      const defaultActiveLaegeKompetence = allLaegeKompetencerOnFirstAvailableCategory.firstOrDefault();
      if (defaultActiveLaegeKompetence) {
        dispatch(setActiveLaegekompetenceMeta(defaultActiveLaegeKompetence.ToState()))
      } else {
        dispatch(removeActiveLaegekompetenceMeta())
      }
    }
    handleSetActiveKompetence();
  }, [activeLaegeKompetenceId, brugerMaalbeskrivelseIdToUse, dispatch, isKlarmeldingerPage, isLoading, isMobile, laegeKompetencerMeta.length, laegekompetenceCategoriesState]);

  const patchLaegeKompetencer = (updatedLaegeKompetencer: LaegekompetenceMeta | LaegekompetenceMeta[]) => {
    const updatesArray = Array.isArray(updatedLaegeKompetencer) ? updatedLaegeKompetencer : [updatedLaegeKompetencer];
    const updatesMap = new Map(updatesArray.map(lk => [lk.laegekompetenceId, lk]));

    setLaegeKompetencerMeta(prev => 
      prev
        .map(lk => updatesMap.get(lk.laegekompetenceId) || lk)
        .filter(lk => 
          !isKlarmeldingerPage || lk.status === LaegekompetenceStatusEnum.Klarmeldt //Klarmeldinger-page should only show klarmeldte-kompetencer
        )
    )
  }
  
  return {
    laegeKompetencerTotalCount: laegekompetenceCategoriesState.flatMap(x => x.laegekompetencer).length,
    laegeKompetenceCategories: laegekompetenceCategoriesState,
    laegeKompetenceCategoriesFiltered: laegekompetenceCategoriesState.map(lkc => ({
      ...lkc,
      laegekompetencer: filterLaegeKompetenceMetas(lkc.laegekompetencer, filter.kompetenceStatus, filter.searchParam)
    } as KompetenceCardCategory)),
    kompetenceIdsAndFileCounts,
    isLoading,
    refetch: fetchLaegeKompetencer,
    patchLaegeKompetencer,
  };
}

export default useKompetenceCardListCategories;